const DAY_MS = 86_400_000;
/** `5.25` → `{hours: 5, minutes: 15}`. Rounded to the minute; monday never emits finer. */
export function splitHour(hour) {
    const hours = Math.floor(hour);
    const minutes = Math.round((hour - hours) * 60);
    return { hours, minutes };
}
/** UTC midnight of the day a `Date` falls on. */
export function startOfUtcDay(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}
export function addUtcDays(date, days) {
    return new Date(date.getTime() + days * DAY_MS);
}
export function addUtcMonths(date, months) {
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth() + months;
    const day = date.getUTCDate();
    // Clamp to the last day of the target month, which is what a Monthly `days:["31"]` needs.
    const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    return new Date(Date.UTC(year, month, Math.min(day, lastDay), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds()));
}
/** Applies a signed `{count, unit}` offset to a **date** (calendar arithmetic, UTC). */
export function applyOffset(date, offset) {
    const { count, unit } = offset;
    if (count === 0)
        return date;
    switch (unit) {
        case 'months':
            return addUtcMonths(date, count);
        case 'weeks':
            return addUtcDays(date, count * 7);
        case 'businessDays':
            return addBusinessDays(date, count);
        case 'hours':
            return new Date(date.getTime() + count * 3_600_000);
        case 'minutes':
            return new Date(date.getTime() + count * 60_000);
        case 'days':
        default:
            return addUtcDays(date, count);
    }
}
/** Mon–Fri only; `count` may be negative. The builder offers it as a duration unit (E14). */
export function addBusinessDays(date, count) {
    const step = count >= 0 ? 1 : -1;
    let remaining = Math.abs(count);
    let cursor = date;
    while (remaining > 0) {
        cursor = addUtcDays(cursor, step);
        const weekday = cursor.getUTCDay();
        if (weekday !== 0 && weekday !== 6)
            remaining -= 1;
    }
    return cursor;
}
/**
 * Block `580` "date arrives", with its offset.
 *
 *     due_at = startOfUtcDay(cellDate + offset) + utcDaysDiff days + hour hours   // all UTC
 *
 * Returns `null` for an empty cell — an unset date never schedules anything.
 */
export function dateTriggerDueAt(config, cellDate) {
    if (cellDate === null)
        return null;
    const shifted = applyOffset(startOfUtcDay(cellDate), config.offset);
    const day = addUtcDays(startOfUtcDay(shifted), config.utcDaysDiff);
    const { hours, minutes } = splitHour(config.hour);
    return new Date(day.getTime() + hours * 3_600_000 + minutes * 60_000);
}
/** ISO weekday of a UTC date: 1 = Monday … 7 = Sunday, the numbering monday uses (E2). */
export function isoWeekday(date) {
    const day = date.getUTCDay();
    return day === 0 ? 7 : day;
}
/**
 * Block `47` "every time period". Expands a recurrence into the due points inside
 * `[after, until]`, exclusive of `after` so a tick never re-emits the point it just fired.
 *
 * `occurrences` is the **interval multiplier** ("every N weeks"), confirmed on the sandbox (E1);
 * `days` is ISO weekdays for `Weekly` and days of the month for `Monthly`, and a Monthly `31`
 * clamps to the last day of a short month.
 */
export function recurrenceDuePoints(config, after, until, options = {}) {
    const { hours, minutes } = splitHour(config.hour);
    const every = Math.max(1, config.occurrences);
    const timeOfDay = hours * 3_600_000 + minutes * 60_000;
    const anchor = startOfUtcDay(options.anchor ?? after);
    const out = [];
    const emit = (day) => {
        const at = new Date(startOfUtcDay(day).getTime() + timeOfDay);
        if (at.getTime() > after.getTime() && at.getTime() <= until.getTime())
            out.push(at);
    };
    switch (config.type) {
        case 'Daily': {
            for (let cursor = startOfUtcDay(after); cursor.getTime() <= until.getTime(); cursor = addUtcDays(cursor, 1)) {
                const elapsed = Math.round((cursor.getTime() - anchor.getTime()) / DAY_MS);
                if (mod(elapsed, every) === 0)
                    emit(cursor);
            }
            break;
        }
        case 'Weekly': {
            const wanted = new Set(config.days.length > 0 ? config.days : [isoWeekday(anchor)]);
            const anchorWeek = weekIndex(anchor);
            for (let cursor = startOfUtcDay(after); cursor.getTime() <= until.getTime(); cursor = addUtcDays(cursor, 1)) {
                if (!wanted.has(isoWeekday(cursor)))
                    continue;
                if (mod(weekIndex(cursor) - anchorWeek, every) !== 0)
                    continue;
                emit(cursor);
            }
            break;
        }
        case 'Monthly': {
            const wanted = config.days.length > 0 ? config.days : [anchor.getUTCDate()];
            let month = new Date(Date.UTC(after.getUTCFullYear(), after.getUTCMonth(), 1));
            const anchorMonths = anchor.getUTCFullYear() * 12 + anchor.getUTCMonth();
            while (month.getTime() <= until.getTime()) {
                const months = month.getUTCFullYear() * 12 + month.getUTCMonth();
                if (mod(months - anchorMonths, every) === 0) {
                    const lastDay = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 0)).getUTCDate();
                    for (const wantedDay of wanted) {
                        emit(new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), Math.min(wantedDay, lastDay))));
                    }
                }
                month = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 1));
            }
            break;
        }
        case 'Yearly': {
            const wantedMonths = config.months.length > 0 ? config.months : [anchor.getUTCMonth() + 1];
            const wantedDays = config.days.length > 0 ? config.days : [anchor.getUTCDate()];
            for (let year = after.getUTCFullYear(); year <= until.getUTCFullYear(); year += 1) {
                if (mod(year - anchor.getUTCFullYear(), every) !== 0)
                    continue;
                for (const month of wantedMonths) {
                    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
                    for (const day of wantedDays)
                        emit(new Date(Date.UTC(year, month - 1, Math.min(day, lastDay))));
                }
            }
            break;
        }
    }
    return out.sort((a, b) => a.getTime() - b.getTime());
}
/** Whole weeks since the epoch, Monday-based, so "every N weeks" has a stable phase. */
function weekIndex(date) {
    const monday = addUtcDays(startOfUtcDay(date), -(isoWeekday(date) - 1));
    return Math.round(monday.getTime() / (7 * DAY_MS));
}
/** Positive modulo; `-1 % 3` is `-1` in JavaScript and that would break the phase test. */
function mod(value, divisor) {
    return ((value % divisor) + divisor) % divisor;
}
/** A 5-field cron (`"0 8 21 9 *"`, UTC) — the only shape `oneOff` recipes use (§5.6). */
export function nextCronOccurrence(expression, after) {
    const parts = expression.trim().split(/\s+/);
    if (parts.length !== 5)
        return null;
    const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;
    const matches = (field, value) => field === '*' || field.split(',').some((entry) => Number(entry) === value);
    // A one-off cron is at most a year away; a minute-by-minute walk over that is cheap enough and
    // has no edge cases of its own.
    let cursor = new Date(Math.ceil((after.getTime() + 1) / 60_000) * 60_000);
    const limit = new Date(after.getTime() + 366 * DAY_MS);
    while (cursor.getTime() <= limit.getTime()) {
        if (matches(minute, cursor.getUTCMinutes()) &&
            matches(hour, cursor.getUTCHours()) &&
            matches(dayOfMonth, cursor.getUTCDate()) &&
            matches(month, cursor.getUTCMonth() + 1) &&
            matches(dayOfWeek, cursor.getUTCDay())) {
            return cursor;
        }
        cursor = new Date(cursor.getTime() + 60_000);
    }
    return null;
}
//# sourceMappingURL=schedule.js.map